import { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import {
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Paper,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    DialogActions,
    Button,
    Table,
    Box,
    Skeleton
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTableRow from "../Row/CustomTableRow";

/*
 * Sækir tímaskráningar
 */
async function fetchTimeEntries(employeeId) {
    const response = await axios.get(`/api/timeregistration/GetTimeRegistrationReportList/${employeeId}`);
    if (response.status !== 200) throw new Error(`fetchTimeEntries: ${response.status}`);
    return response.data ?? [];
}

const TimeReportDialog = ({ open, onClose, employeeId }) => {
    const [periods, setPeriods] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const { data, isLoading } = useQuery(
        ["timeEntries", employeeId],
        () => fetchTimeEntries(employeeId),
        {
            refetchInterval: 300000,
            refetchOnWindowFocus: true,
            enabled: open
        }
    );

    useEffect(() => {
        if (data?.length) {
            setPeriods(data);
            setSelectedIndex(0); // Current period is always first
        }
    }, [data]);

    const selectedPeriod = periods[selectedIndex];
    const isNewestPeriod = selectedIndex === 0;
    const isOldestPeriod = selectedIndex === periods.length - 1;

    // Navigate to newer period (towards index 0)
    const handleGoForward = () => {
        if (isNewestPeriod) return;
        setSelectedIndex(prev => prev - 1);
    };

    // Navigate to older period (towards higher index)
    const handleGoBack = () => {
        if (isOldestPeriod) return;
        setSelectedIndex(prev => prev + 1);
    };

    const calcTotal = () => {
        if (!selectedPeriod?.entries?.length) return "0.00";
        return selectedPeriod.entries
            .reduce((sum, { totalTime }) => sum + totalTime, 0)
            .toFixed(2);
    };

    return (
        <Dialog open={open} maxWidth="lg" fullWidth onClose={onClose}>
            <DialogTitle>
                Time Overview
                <Typography>Total time: {calcTotal()}</Typography>
            </DialogTitle>
            <DialogContent sx={{ height: "70vh" }}>
                <Paper sx={{ flexGrow: 1 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="text" sx={{ fontSize: '1rem', width: '7rem' }} />
                            <Skeleton variant="circular" width={40} height={40} />
                        </Box>
                    ) : (
                        <div style={{ textAlign: "center" }}>
                            <IconButton 
                                color="inherit" 
                                onClick={handleGoBack}
                                disabled={isOldestPeriod}
                            >
                                <KeyboardArrowLeft fontSize="large" />
                            </IconButton>
                            <span>{selectedPeriod?.label ?? ""}</span>
                            <IconButton 
                                color="inherit" 
                                onClick={handleGoForward}
                                disabled={isNewestPeriod}
                            >
                                <KeyboardArrowRight fontSize="large" />
                            </IconButton>
                        </div>
                    )}
                    <TableContainer sx={{ maxHeight: "60vh" }}>
                        {isLoading ? (
                            <Skeleton height={200} />
                        ) : (
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Date</TableCell>
                                        <TableCell align="right">Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedPeriod?.entries?.map(row => (
                                        <CustomTableRow key={row.id} row={row} />
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </TableContainer>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TimeReportDialog;